/**
 * Choosing, uploading and removing a design's images (DESIGN §9.1, §6.2).
 *
 * The thumbnails come from presigned URLs the API hands back with the detail
 * view: an `<img>` cannot carry a bearer token, so the server never asks it to.
 */

import { useRef, useState } from "react";

import { ErrorBox } from "../components/common";
import { useI18n } from "../i18n";
import type { ScreenDesignAsset } from "../api/types";
import { LIMITS } from "./spec";

export default function AssetPicker({
  assets,
  value,
  uploading,
  error,
  onPick,
  onUpload,
  onDelete,
  allowNone,
}: {
  assets: ScreenDesignAsset[];
  value: string | null;
  uploading: boolean;
  error?: unknown;
  onPick: (assetId: string) => void;
  onUpload: (file: File) => void;
  onDelete?: (assetId: string) => void;
  allowNone?: boolean;
}) {
  const { t } = useI18n();
  const input = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const full = assets.length >= LIMITS.assets;

  return (
    <div className="design-assets">
      <div className="design-asset-grid">
        {allowNone && (
          <button
            type="button"
            className={value === null ? "design-asset on" : "design-asset"}
            onClick={() => onPick("")}
          >
            <span className="muted small">{t("design.noImage")}</span>
          </button>
        )}
        {assets.map((asset) => (
          <span key={asset.id} className="design-asset-slot">
            <button
              type="button"
              className={asset.id === value ? "design-asset on" : "design-asset"}
              onClick={() => onPick(asset.id)}
              title={`${asset.width}×${asset.height}`}
            >
              <img src={asset.url} alt="" />
            </button>
            {onDelete && (
              <button
                type="button"
                className="design-asset-remove danger"
                aria-label={t("design.removeImage")}
                title={t("design.removeImage")}
                onClick={() => onDelete(asset.id)}
              >
                ✕
              </button>
            )}
          </span>
        ))}
      </div>

      <div
        className={dragging ? "design-drop on" : "design-drop"}
        onDragOver={(event) => {
          event.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(event) => {
          event.preventDefault();
          setDragging(false);
          const file = event.dataTransfer.files?.[0];
          if (file && !full) onUpload(file);
        }}
      >
        <input
          ref={input}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          hidden
          onChange={(event) => {
            const file = event.target.files?.[0];
            if (file) onUpload(file);
            event.target.value = "";
          }}
        />
        <button
          type="button"
          disabled={uploading || full}
          onClick={() => input.current?.click()}
        >
          {uploading ? t("design.uploading") : t("design.uploadImage")}
        </button>
        <span className="muted small">
          {full
            ? t("design.assetsFull", { max: LIMITS.assets })
            : t("design.uploadHint", { used: assets.length, max: LIMITS.assets })}
        </span>
      </div>
      {error ? <ErrorBox error={error} /> : null}
    </div>
  );
}
